import React, { useRef, useState } from "react";
import { PageHeader } from "@/components/shell/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformBadge } from "@/components/badges/PlatformBadge";
import { StatusBadge } from "@/components/badges/StatusBadge";
import { ApprovalActions } from "@/components/content/ApprovalActions";
import { PLATFORMS, PLATFORM_LABELS, CONTENT_STATUSES, STATUS_LABELS } from "@/lib/constants";
import { Image as ImageIcon, CalendarPlus, Save, Eye, CheckCheck, Loader2 } from "lucide-react";
import { useAppState } from "@/state/AppStateProvider";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function ManualPost() {
  const { addAsset, scheduleAsset, approveAsset, uploadImage } = useAppState();
  const [title, setTitle] = useState("My take on agent skill libraries");
  const [platform, setPlatform] = useState("linkedin");
  const [body, setBody] = useState("Agents don't get smarter when you write better prompts. They get smarter when you give them a library of skills with phases.\n\nA skill is a contract: when to use, what to produce, what to refuse. That's it. The unlock isn't the model. It's the routing.");
  const [status, setStatus] = useState("draft");
  const [scheduleAt, setScheduleAt] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const [attachedImageName, setAttachedImageName] = useState("");
  const [imageBusy, setImageBusy] = useState(false);
  const imageInputRef = useRef(null);
  const charLimit = platform === "x" ? 280 : 3000;

  const buildAsset = (coverImage = attachedImage) => ({
    id: `asset_${Math.random().toString(36).slice(2, 8)}`,
    project_id: null,
    title,
    platform,
    asset_type: "manual_post",
    status,
    skill_used: "—",
    hook: body.split("\n")[0] || "",
    body,
    cta: "",
    hashtags: [],
    char_count: body.length,
    char_limit: charLimit,
    cover_image: coverImage,
    media_name: attachedImageName,
    updated_at: new Date().toISOString(),
    schedule_at: scheduleAt || null,
  });

  const buildAssetWithUploadedImage = async (patch = {}) => {
    let coverImage = attachedImage;
    if (typeof coverImage === "string" && coverImage.startsWith("data:")) {
      setImageBusy(true);
      try {
        const result = await uploadImage(coverImage);
        if (!result?.ok || !result.url) throw new Error(result?.message || "Image upload failed");
        coverImage = result.url;
        setAttachedImage(result.url);
      } finally {
        setImageBusy(false);
      }
    }
    return { ...buildAsset(coverImage), ...patch };
  };

  const onPickImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Choose an image file");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image too large - keep it under 8MB");
    const reader = new FileReader();
    reader.onload = () => {
      setAttachedImage(reader.result);
      setAttachedImageName(file.name);
      toast.success(`${file.name} attached`);
    };
    reader.onerror = () => toast.error("Could not load image");
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  const onSave = async () => {
    try {
      const asset = await buildAssetWithUploadedImage();
      addAsset(asset);
      toast.success("Draft saved");
    } catch (err) {
      toast.error(err.message || "Could not save draft");
    }
  };
  const onApprove = async () => {
    try {
      const a = await buildAssetWithUploadedImage({ status: "approved" });
      addAsset(a);
      approveAsset(a.id);
      toast.success("Approved");
    } catch (err) {
      toast.error(err.message || "Could not approve");
    }
  };
  const onSchedule = async () => {
    if (!scheduleAt) return toast.error("Pick a date/time first");
    try {
      const a = await buildAssetWithUploadedImage({ status: "scheduled" });
      addAsset(a);
      scheduleAsset(a.id, new Date(scheduleAt).toISOString());
      toast.success("Scheduled");
    } catch (err) {
      toast.error(err.message || "Could not schedule");
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="manual-post-page">
      <PageHeader
        eyebrow="Create · Manual"
        title="Manual post editor"
        description="First-class manual creation. Write your own post, preview, and ship through the same approval gate."
      />

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        {/* Editor */}
        <section className="space-y-4 xl:col-span-7">
          <div className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Internal title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="manual-title" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Platform</Label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger data-testid="manual-platform" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => <SelectItem key={p} value={p}>{PLATFORM_LABELS[p]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4 space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Post body</Label>
              <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={12} data-testid="manual-body" className="font-mono text-[13px] leading-[1.65]" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="font-mono">{body.length} / {charLimit}</span>
                <span className="inline-flex items-center gap-1"><CheckCheck className="h-3 w-3 text-[color:var(--accent-green)]" /> Local-only draft</span>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger data-testid="manual-status" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CONTENT_STATUSES.filter((s) => s !== "archived").map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Schedule (optional)</Label>
                <Input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} data-testid="manual-schedule-at" style={{ backgroundColor: "var(--surface-2)", borderColor: "var(--border-subtle)" }} />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3" style={{ borderColor: "var(--border-subtle)", backgroundColor: "var(--surface-2)" }}>
              <div className="flex min-w-0 items-center gap-2 text-sm text-1">
                <ImageIcon className="h-4 w-4 text-[color:var(--accent-cyan)]" />
                <span className="truncate">{attachedImageName ? `Attached: ${attachedImageName}` : "Attach image"}</span>
              </div>
              <div className="flex items-center gap-2">
                {attachedImage && (
                  <Button size="sm" variant="ghost" onClick={() => { setAttachedImage(null); setAttachedImageName(""); }} data-testid="manual-image-remove">Remove</Button>
                )}
                <Button size="sm" variant="outline" onClick={() => imageInputRef.current?.click()} disabled={imageBusy} data-testid="manual-image-attach">
                  {imageBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Attach"}
                </Button>
              </div>
              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} data-testid="manual-image-input" />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button variant="outline" onClick={onSave} disabled={imageBusy} className="gap-1.5" data-testid="manual-save-draft"><Save className="h-4 w-4" /> Save draft</Button>
            <Button variant="outline" onClick={onSchedule} disabled={imageBusy} className="gap-1.5" data-testid="manual-schedule"><CalendarPlus className="h-4 w-4" /> Schedule</Button>
            <Button onClick={onApprove} disabled={imageBusy} className="gap-1.5" data-testid="manual-approve" style={{ backgroundColor: "rgba(22,163,74,0.10)", border: "1px solid rgba(22,163,74,0.24)", color: "#15803D" }}>Approve</Button>
          </div>
        </section>

        {/* Preview */}
        <aside className="space-y-4 xl:col-span-5 xl:sticky xl:top-20 xl:self-start">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold tracking-[-0.005em] text-1"><Eye className="inline h-4 w-4 mr-1.5 text-[color:var(--accent-cyan)]" /> Live preview</h3>
            <StatusBadge status={status} />
          </div>
          <article className="rounded-xl border bg-card p-5 inner-highlight" style={{ borderColor: "var(--border-subtle)" }} data-testid="manual-preview">
            <div className="flex items-center gap-2">
              <PlatformBadge platform={platform} />
              <span className="text-[11px] text-muted-foreground">{platform === "x" ? "Up to 280 chars" : "Long-form supported"}</span>
            </div>
            <h2 className="mt-3 text-base font-semibold text-1">{title}</h2>
            {attachedImage && (
              <img src={attachedImage} alt="" className="mt-3 aspect-video w-full rounded-lg object-cover" data-testid="manual-image-preview" />
            )}
            <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/85 leading-[1.6]">{body || "Your post preview will appear here as you type."}</p>
            <div className="mt-4 border-t pt-3" style={{ borderColor: "var(--border-subtle)" }}>
              <ApprovalActions onApprove={onApprove} onRevise={() => { setStatus("draft"); toast.message("Returned to draft"); }} showRegenerate={false} onReject={() => toast.error("Rejected")} />
            </div>
          </article>
        </aside>
      </div>
    </motion.div>
  );
}
